import React, { useState } from "react"

import {
	Grid,
	Select,
	Typography,
	MenuItem,
	TextField,
	Button,
	CircularProgress,
	FormControlLabel,
	Checkbox
} from "@material-ui/core"

import { ActionDialog, Notification } from "@/components"
import { AccountAdminApi, InboxAdminApi } from "@/services/Api"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import { AccountDataResponse, MassNotifyPayload } from "@/pages/Account/protocols/SubscriptionProtocol"

import useStyles from "@/pages/Tools/ToolsPages/MassBlockAndUnblockToolPage/styles"

const BlockUnblockMassSubscriptionsToolPage = () => {
	const classes = useStyles()
	const { validation, triggerValidation, clearValidation } = useValidation()

	const [actionType, setActionType] = useState("block")
	const [instanceIds, setInstanceIds] = useState("")
	const [loading, setLoading] = useState(false)
	const [openBlockDialog, setOpenBlockDialog] = useState(false)
	const [comunicateSubscriptionBlocking, setComunicateSubscriptionBlocking] = useState(true)

	const options = [
		{ value: "block", label: "Bloquear" },
		{ value: "unblock", label: "Desbloquear" }
	]

	const buttonLabel = () => {
		if (loading && actionType === "block") return "Bloqueando..."
		if (loading && actionType === "unblock") return "Desbloqueando..."
		if (actionType === "block") return "Bloquear"
		if (actionType === "unblock") return "Desbloquear"
		return "Ação"
	}

	const buttonClassByAction: Record<string, string> = {
		block: classes.blockButton,
		unblock: classes.unblockButton
	}

	const selectedActionButtonClass = buttonClassByAction[actionType] ?? classes.disabledActionButton

	const parseInstanceIds = (instanceIds: string): string[] => {
		return instanceIds
			.split(",")
			.map(id => id.trim())
			.filter(entry => entry !== "")
	}

	const fetchAccountAndInstanceIds = async (validInstanceIds: string[]): Promise<MassNotifyPayload[]> => {
		const response = await InboxAdminApi.get<{ data: AccountDataResponse[] }>(
			"/instances/accountData",
			{
				params: {
					instance_ids: validInstanceIds
				}
			}
		)

		const accountAppInstanceIds: MassNotifyPayload[] = response.data.data.map(item => ({
			appInstanceId: item.accountInstanceId,
			accountId: item.ownerUserAccountId
		}))

		return accountAppInstanceIds
	}

	const handleCloseBlockDialog = () => {
		setOpenBlockDialog(false)
	}

	const handleMassSubscriptionAction = async () => {
		setLoading(true)

		const parsedInstanceIds = parseInstanceIds(instanceIds)

		try {
			const accountAppInstanceIds = await fetchAccountAndInstanceIds(parsedInstanceIds)

			await AccountAdminApi.post("/subscriptions/block-or-unblock-mass-subscriptions", {
				accountAppInstanceIds,
				action: actionType,
				...(actionType === "block" && { comunicateSubscriptionBlocking: comunicateSubscriptionBlocking })
			})

			const successMessage = actionType === "block"
				? "Assinaturas bloqueadas com sucesso!"
				: "Assinaturas desbloqueadas com sucesso!"

			Notification.success({ message: successMessage })
			setInstanceIds("")
			handleCloseBlockDialog()
		} catch (error) {
			triggerValidation(error as ErrorType)
			handleCloseBlockDialog()
		} finally {
			setLoading(false)
		}
	}

	const handleMassBlockOrUnblock = async () => {
		if (actionType === "block") {
			setOpenBlockDialog(true)
		} else {
			await handleMassSubscriptionAction()
		}
	}

	return (
		<Grid container spacing={2} direction="column">
			<Grid item>
				<Typography variant="h4">
                    Bloquear/Desbloquear Assinaturas em Massa
				</Typography>
			</Grid>

			<Grid item>
				<Typography color="textSecondary" variant="subtitle1">
                    Insira os IDs das instâncias que você deseja bloquear ou desbloquear <strong>separados por vírgula</strong>.
				</Typography>
			</Grid>

			<Grid item xs={2}>
				<Select
					fullWidth
					variant="outlined"
					displayEmpty
					value={actionType}
					onChange={(e) => setActionType(e.target.value as "block" | "unblock")}
				>
					<MenuItem value="" disabled>
						<Typography color="textSecondary">Escolha uma opção</Typography>
					</MenuItem>

					{options.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</Grid>

			<Grid item md={8} className={classes.textArea}>
				<TextField
					label="IDs das Instâncias"
					placeholder="Ex: 7524, 5644, 7168..."
					multiline
					rows={8}
					variant="outlined"
					fullWidth
					value={instanceIds}
					onChange={(e) => {
						setInstanceIds(e.target.value)
						clearValidation("instanceIds")
					}}
					className={classes.textArea}
					error={!!validation.instanceIds}
					helperText={validation.instanceIds}
				/>
			</Grid>

			<Grid item>
				<Button
					variant="contained"
					onClick={handleMassBlockOrUnblock}
					disabled={loading || !instanceIds || !actionType}
					endIcon={loading && <CircularProgress size={20} color="inherit" />}
					className={selectedActionButtonClass}
				>
					{buttonLabel()}
				</Button>

				<ActionDialog
					customId={"mass-block-subscription"}
					title={"Bloquear assinaturas em massa"}
					onSave={handleMassSubscriptionAction}
					saveText="Bloquear"
					onClose={handleCloseBlockDialog}
					openDialog={openBlockDialog}
					loading={loading}
					fullWidth
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControlLabel
								label="Comunicar clientes do bloqueio?"
								control={
									<Checkbox
										id="comunicate-mass-block-subscription-checkbox"
										checked={comunicateSubscriptionBlocking}
										onChange={({ target }) => {
											setComunicateSubscriptionBlocking(target.checked)
										}}
									/>
								}
							/>
						</Grid>
					</Grid>
				</ActionDialog>
			</Grid>
		</Grid>
	)
}

export default BlockUnblockMassSubscriptionsToolPage
